import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import {
  ArrowLeft,
  Loader,
  X,
  ChevronDown,
  CheckIcon,
  Download,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { UserActionDialog } from '../UserActionDialog'
import { useState, useEffect } from 'react'
import { EnvelopeIcon, IssueRefundIcon } from '../Icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getAttendeeById, downloadAttendeeConsentForm } from '@/api/attendeeApi'
import { ATTENDEE_QUERY_KEYS, EVENT_QUERY_KEYS } from '@/api/queryKeys'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AttendeeDetails } from '@/schemas/attendee.schema'
import CustomLoader from '../CustomLoader'
import {
  formatAddress,
  formatDateTimeIntl,
  handleAxiosError,
  isUserSuperAdmin,
  paymentStatusClasses,
  calculateRefundAmount,
  formatDateIntl,
  safeDownloadFile,
  formatPaymentStatusText,
} from '@/utils/helper'
import { showSuccessToast } from '../ui/toast'
import { initiateRefund } from '@/api/eventApi'
import {
  PAYMENT_STATUS,
  REFUND_PERCENTAGES,
  DEFAULT_REFUND_PERCENTAGE,
} from '@/constants'
import { attendeePersona, attendeeSkiExperience } from '@/constants/attendee'

interface AttendeeDetailDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  attendeeId: string
  onBackToEventDetails: () => void
  eventId: string
  onResendTicketClick: (transactionId: string) => void
  isResending?: boolean
  showRefundButton?: boolean
}

export default function AttendeeDetailDrawer({
  open,
  onOpenChange,
  onBackToEventDetails,
  eventId,
  onResendTicketClick,
  isResending = false,
  showRefundButton = false,
}: Readonly<Omit<AttendeeDetailDrawerProps, 'attendeeId'>>) {
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  const [refundPercent, setRefundPercent] = useState(DEFAULT_REFUND_PERCENTAGE)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [downloadingAttendeeId, setDownloadingAttendeeId] = useState<
    string | null
  >(null)
  const queryClient = useQueryClient()
  const isSuperAdmin = isUserSuperAdmin()

  useEffect(() => {
    if (open) {
      setRefundPercent(DEFAULT_REFUND_PERCENTAGE)
      setDropdownOpen(false)
    }
  }, [open, eventId])

  const { data: attendeesData, isLoading } = useQuery({
    queryKey: ATTENDEE_QUERY_KEYS.byId(eventId),
    queryFn: () => getAttendeeById(eventId),
    enabled: !!eventId && open,
  })

  const { mutate: initiateRefundMutation, isPending: isInitiatingRefund } =
    useMutation({
      mutationFn: ({
        transactionId,
        refundPercent,
      }: {
        transactionId: string
        refundPercent: number
      }) => initiateRefund(transactionId, refundPercent),
      onSuccess: () => {
        showSuccessToast('Refund initiated successfully')
        queryClient.invalidateQueries({
          queryKey: [ATTENDEE_QUERY_KEYS.all],
        })
        queryClient.invalidateQueries({
          queryKey: ATTENDEE_QUERY_KEYS.byId(eventId),
        })
        queryClient.invalidateQueries({
          queryKey: [EVENT_QUERY_KEYS.all],
        })
        queryClient.invalidateQueries({
          queryKey: EVENT_QUERY_KEYS.eventStatistics,
        })
        setRefundDialogOpen(false)
        setDropdownOpen(false)
      },
      onError: error => {
        handleAxiosError('Failed to initiate refund', error)
        setRefundDialogOpen(false)
        setDropdownOpen(false)
      },
    })

  const attendeeDetails: AttendeeDetails = attendeesData?.data

  const getPaymentStatusVariant = (status: string) => {
    switch (status) {
      case 'Confirmed':
        return 'default'
      case 'Pending':
        return 'secondary'
      case 'Failed':
        return 'destructive'
      case 'Refunded':
      case 'Partially Refunded':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const { mutate: downloadConsentForm } = useMutation({
    mutationFn: ({
      consentFormKey,
      attendeeName,
      attendeeId,
    }: {
      consentFormKey: string
      attendeeName: string
      attendeeId: string
    }) =>
      downloadAttendeeConsentForm(consentFormKey).then(blob => ({
        blob,
        attendeeName,
        attendeeId,
      })),
    onSuccess: ({ blob, attendeeName, attendeeId }) => {
      safeDownloadFile({
        data: blob,
        fileName: `consent_form_${attendeeId}-${attendeeName.replaceAll(/\s+/g, '_')}.pdf`,
      })
      showSuccessToast('Consent form downloaded successfully')
      setDownloadingAttendeeId(null)
    },
    onError: err => {
      handleAxiosError(
        'Failed to download consent form. Please try again.',
        err
      )
      setDownloadingAttendeeId(null)
    },
  })

  const handleDownloadConsentForm = (
    attendeeId: string,
    attendeeName: string,
    consentFormKey: string
  ) => {
    setDownloadingAttendeeId(attendeeId)
    downloadConsentForm({ consentFormKey, attendeeName, attendeeId })
  }

  const handleInitiateRefund = () => {
    if (attendeeDetails) {
      initiateRefundMutation({
        transactionId: attendeeDetails.id,
        refundPercent,
      })
    }
  }

  const isShowRefundResentButton =
    attendeeDetails?.status === PAYMENT_STATUS.COMPLETED ||
    attendeeDetails?.status === PAYMENT_STATUS.COMPLETED_MANUALLY

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent
        aria-labelledby="attendee-details-title"
        aria-label="Attendee details drawer"
        className="h-full p-0 w-full max-w-max lg:max-w-[750px] rounded-none lg:rounded-xl transition-all duration-900 ease-in-out"
      >
        {/* Header */}
        <DrawerHeader className="border-b border-ss-neutral-50 px-4 py-3 animate-in fade-in-50 slide-in-from-top-2 duration-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBackToEventDetails}
                aria-label="Back to event details"
                className="h-5 w-5 p-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <DrawerTitle
                id="attendee-details-title"
                className="text-ss-text-primary text-lg font-semibold leading-7"
              >
                Attendee Details
              </DrawerTitle>
            </div>
            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close attendee details drawer"
                className="h-5 w-5 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        {/* Content */}
        {isLoading || !attendeeDetails ? (
          <CustomLoader />
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-4 animate-in fade-in-50 slide-in-from-right-5 duration-300">
            {/* Attendee Header Section */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-2.5">
              <Avatar className="h-16 w-16">
                <AvatarImage
                  src={attendeeDetails.user?.profilePicture?.url}
                  alt={`${attendeeDetails.user?.firstName} ${attendeeDetails.user?.lastName}`}
                />
                <AvatarFallback className="bg-ss-neutral-200 text-ss-text-primary font-semibold">
                  {`${attendeeDetails.user?.firstName[0]} ${attendeeDetails.user?.lastName[0]}`
                    .split(' ')
                    .map(n => n[0])
                    .join('')}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 space-y-0.5">
                <h3 className="text-ss-text-primary text-base font-semibold leading-[22px]">
                  {`${attendeeDetails.user?.firstName} ${attendeeDetails.user?.lastName}`}
                </h3>
                <p className="text-ss-text-secondary text-sm leading-4.5 ">
                  {attendeeDetails.event?.name}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                {showRefundButton &&
                  isShowRefundResentButton &&
                  isSuperAdmin && (
                    <DropdownMenu
                      open={dropdownOpen}
                      onOpenChange={setDropdownOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 border-ss-neutral-200 justify-center"
                        >
                          <IssueRefundIcon className="h-4 w-4 mr-1" />
                          Issue Refund ({refundPercent}%)
                          <ChevronDown className="h-4 w-4 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-48 p-2 space-y-1"
                      >
                        <div className="px-2 py-1.5 text-xs font-semibold text-ss-neutral-600">
                          Select Refund Amount
                        </div>
                        {REFUND_PERCENTAGES.map(option => (
                          <DropdownMenuItem
                            key={option.value}
                            onSelect={e => {
                              e.preventDefault()
                              setRefundPercent(option.value)
                            }}
                            className={cn(
                              'cursor-pointer',
                              refundPercent === option.value && 'bg-ss-blue-50'
                            )}
                          >
                            <span className="flex-1">{option.label}</span>
                            {refundPercent === option.value && (
                              <CheckIcon className="text-ss-blue-900" />
                            )}
                          </DropdownMenuItem>
                        ))}
                        <Separator className="my-2" />
                        <Button
                          size="sm"
                          className="w-full bg-ss-blue-900 text-white hover:bg-ss-blue-800"
                          onClick={() => {
                            setRefundDialogOpen(true)
                            setDropdownOpen(false)
                          }}
                        >
                          <IssueRefundIcon className="h-4 w-4 mr-1" />
                          Issue Refund
                        </Button>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                {isShowRefundResentButton && (
                  <Button
                    size="sm"
                    className="h-9 bg-ss-blue-900 text-white hover:bg-ss-blue-800 justify-center"
                    onClick={() => onResendTicketClick(attendeeDetails.id)}
                    disabled={isResending}
                  >
                    {isResending ? (
                      <>
                        <Loader
                          className="size-5 animate-spin"
                          aria-hidden="true"
                        />
                        Re-Sending Ticket...
                      </>
                    ) : (
                      <>
                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                        Re-Send Ticket
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            <Separator className="bg-ss-neutral-50" />

            {/* Personal Info Section */}
            <section className="space-y-3">
              <h3 className="text-ss-text-primary text-lg font-semibold leading-7">
                Personal Info
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-12">
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Email
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    {attendeeDetails.user?.email}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Phone
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    {attendeeDetails.phone}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    City / State / Country
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    {formatAddress({
                      state: attendeeDetails.state,
                      city: attendeeDetails.city,
                      country: attendeeDetails.country,
                      zipCode: attendeeDetails.zipCode,
                      street: attendeeDetails.street,
                    })}
                  </p>
                </div>
              </div>
            </section>

            <Separator className="bg-ss-neutral-50" />

            {/* Ticket & Registration Info Section */}
            <section className="space-y-3">
              <h3 className="text-ss-text-primary text-lg font-semibold leading-7">
                Ticket & Registration Info
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-12">
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Event Name
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    {attendeeDetails.event?.name}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Registration Date & Time
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    {formatDateTimeIntl(attendeeDetails.createdAt)}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Ticket ID
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    {attendeeDetails.id}
                  </p>
                </div>
              </div>
            </section>

            {/* Tickets Purchased Section */}
            <section className="border border-ss-neutral-50 rounded-lg p-4 space-y-3">
              <h3 className="text-ss-text-primary text-lg font-semibold leading-7">
                Tickets Purchased ({attendeeDetails.totalQuantity} Tickets)
              </h3>
              <div className="space-y-3">
                {attendeeDetails.items.map((ticket, index) => (
                  <div
                    key={`${ticket?.ticket?.name}${index}`}
                    className="border border-ss-neutral-50 rounded-lg p-3 space-y-2.5 hover:bg-ss-neutral-25 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-ss-text-primary font-semibold text-base leading-[22px]">
                            {ticket.ticket.name}
                          </span>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-xs font-medium rounded-full leading-4 px-2 py-0.5',
                              ticket.ticket.isSubEventTicket
                                ? 'bg-ss-blue-50 text-ss-blue-900 border-ss-blue-200'
                                : 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100'
                            )}
                          >
                            {ticket.ticket.isSubEventTicket
                              ? 'Sub-Event'
                              : 'Main Event'}
                          </Badge>
                        </div>

                        <p className="text-ss-text-secondary text-sm leading-4.5">
                          Quantity:{' '}
                          <span className="text-ss-text-primary font-semibold">
                            {ticket.quantity} Ticket
                            {ticket.quantity > 1 ? 's' : ''}
                          </span>
                        </p>
                      </div>

                      <Badge
                        className={cn(
                          'px-2 py-1 text-xs font-medium rounded-full',
                          paymentStatusClasses(ticket.status)
                        )}
                        aria-label={`Status: ${ticket.status}`}
                      >
                        {formatPaymentStatusText(ticket.status)}
                      </Badge>
                    </div>

                    <div className="pt-2 border-t border-ss-neutral-50">
                      <div className="flex items-center justify-between">
                        <span className="text-ss-text-secondary text-sm leading-4.5">
                          Total Amount
                        </span>
                        <div className="text-right">
                          <p className="text-ss-text-primary text-base font-bold leading-[22px]">
                            $
                            {isSuperAdmin
                              ? ticket.totalAmount
                              : ticket.baseAmount}
                          </p>
                          {isSuperAdmin && Number(ticket?.baseAmount) !== 0 && (
                            <p className="text-ss-text-secondary text-xs leading-4 mt-0.5">
                              ${ticket?.baseAmount} + ${ticket?.feeAmount} fee
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <Separator className="bg-ss-neutral-50" />

            {/* Attendees Section */}
            {attendeeDetails.attendees &&
              attendeeDetails.attendees.length > 0 && (
                <>
                  <section className="space-y-2">
                    <h3 className="text-ss-text-primary text-lg font-semibold leading-7">
                      Attendees ({attendeeDetails.attendees.length})
                    </h3>
                    <div className="space-y-2">
                      {attendeeDetails.attendees.map((attendee, index) => (
                        <div
                          key={attendee.id}
                          className="border border-ss-neutral-50 rounded-lg p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="text-ss-text-primary text-base font-semibold leading-[22px]">
                              Attendee {index + 1}
                            </h4>
                            {attendee.isConsentFormSigned &&
                              attendee.signedConsentFormUrl &&
                              isSuperAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 border-ss-neutral-200"
                                  onClick={() =>
                                    handleDownloadConsentForm(
                                      attendee?.id,
                                      attendee?.name,
                                      attendee?.signedConsentFormUrl as string
                                    )
                                  }
                                  disabled={
                                    downloadingAttendeeId === attendee.id
                                  }
                                >
                                  {downloadingAttendeeId === attendee.id ? (
                                    <>
                                      <Loader
                                        className="size-4 animate-spin mr-1"
                                        aria-hidden="true"
                                      />
                                      Downloading...
                                    </>
                                  ) : (
                                    <>
                                      <Download className="h-4 w-4 mr-1" />
                                      Download Consent Form
                                    </>
                                  )}
                                </Button>
                              )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Name
                              </span>
                              <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                {attendee.name}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Email
                              </span>
                              <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                {attendee.email}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Birth Date
                              </span>
                              <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                {formatDateIntl(attendee.birthDate)}
                              </p>
                            </div>
                            <div className="space-y-1 flex flex-col items-start">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Consent Form Signed
                              </span>
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-xs font-medium rounded-full leading-4 px-2 py-0.5',
                                  attendee.isConsentFormSigned
                                    ? 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100'
                                    : 'bg-ss-critical-50 text-ss-critical-200 border-ss-critical-100'
                                )}
                              >
                                {attendee.isConsentFormSigned ? 'Yes' : 'No'}
                              </Badge>
                            </div>
                            {attendee.isConsentFormSigned &&
                              attendee.consentFormSignedAt && (
                                <div className="space-y-1">
                                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                    Consent Signed Date
                                  </span>
                                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                    {formatDateTimeIntl(
                                      attendee.consentFormSignedAt
                                    )}
                                  </p>
                                </div>
                              )}
                            <div className="space-y-1">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                IP Address
                              </span>
                              <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                {attendee.ipAddress || 'N/A'}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Persona
                              </span>
                              <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                {attendeePersona[
                                  attendee?.persona as keyof typeof attendeePersona
                                ] || 'N/A'}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Experience Tag
                              </span>
                              <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                {attendeeSkiExperience[
                                  attendee?.experience as keyof typeof attendeeSkiExperience
                                ] || 'N/A'}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Booking Status
                              </span>
                              <Badge
                                className={cn(
                                  'px-2 py-1 text-xs font-medium rounded-full block',
                                  paymentStatusClasses(attendee.status)
                                )}
                                aria-label={`Status: ${attendee.status}`}
                              >
                                {formatPaymentStatusText(attendee.status)}
                              </Badge>
                            </div>
                          </div>
                          {attendee.reconcileReason && (
                            <div>
                              <span className="text-ss-text-secondary text-sm leading-4.5 ">
                                Reconcile Reason
                              </span>
                              <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                                {attendee.reconcileReason}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  <Separator className="bg-ss-neutral-50" />
                </>
              )}

            {/* Payment Information Section */}
            <section className="space-y-3">
              <h3 className="text-ss-text-primary text-lg font-semibold leading-7">
                Payment Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(auto,120px)_1fr_1fr_2fr] gap-6 lg:gap-12">
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5  mr-2 mb-1">
                    Payment Status
                  </span>
                  <Badge
                    variant={getPaymentStatusVariant(attendeeDetails.status)}
                    className={cn(
                      'text-xs font-medium rounded-full leading-4 block',
                      paymentStatusClasses(attendeeDetails.status)
                    )}
                  >
                    {formatPaymentStatusText(attendeeDetails.status)}
                  </Badge>
                </div>
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Transaction ID
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    {attendeeDetails.transactionId
                      ? attendeeDetails.transactionId
                      : 'N/A'}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Payment Method
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    Stripe
                  </p>
                </div>
                <div className="space-y-1.5">
                  <span className="text-ss-text-secondary text-sm leading-4.5 ">
                    Amount Paid
                  </span>
                  <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                    $
                    {isSuperAdmin
                      ? `${attendeeDetails.totalAmount}`
                      : `${attendeeDetails.baseAmount}`}
                    {isSuperAdmin && (
                      <span>
                        ($
                        {attendeeDetails.baseAmount} + $
                        {attendeeDetails.feeAmount} fee)
                      </span>
                    )}
                  </p>
                </div>
                {attendeeDetails.status === PAYMENT_STATUS.REFUNDED && (
                  <div className="space-y-1.5">
                    <span className="text-ss-text-secondary text-sm leading-4.5 ">
                      Refunded Amount
                    </span>
                    <p className="text-ss-text-primary text-base font-semibold leading-[22px] wrap-anywhere">
                      ${attendeeDetails.refundAmount ?? '0.00'}
                    </p>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {/* Footer */}
        <DrawerFooter className="border-t border-ss-neutral-50 bg-white px-6 py-4 shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)] rounded-bl-none rounded-br-none lg:rounded-bl-xl lg:rounded-br-xl animate-in fade-in-50 slide-in-from-bottom-2 duration-200">
          <div className="flex justify-end">
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-ss-blue-900 text-white hover:bg-ss-blue-800"
              aria-label="Close attendee details drawer"
            >
              OK
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>

      {/* Issue Refund Dialog */}
      <UserActionDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        onConfirm={handleInitiateRefund}
        title="Are you sure you want to confirm refund?"
        subtext={`Are you sure you want to refund ${refundPercent}% (${
          attendeeDetails?.totalAmount
            ? calculateRefundAmount(attendeeDetails.totalAmount, refundPercent)
            : '$0.00'
        }) for ${attendeeDetails?.name}'s ticket? This action cannot be undone.`}
        confirmButtonText="Issue Refund"
        danger={true}
        showForm={false}
        isLoading={isInitiatingRefund}
      />
    </Drawer>
  )
}
